import * as Styled from './Orders.styles';

import { FC, useEffect } from 'react';
import { useDebounce, sendGAEvent } from 'utils/utils';
import { useHistory, useParams } from 'react-router-dom';

import { Icon } from 'features/common/components/Icon';
import { IdParamsType } from 'utils/types';
import { MainHeader } from 'features/common/components/MainHeader';
import { MainLayout } from 'layouts/MainLayout/MainLayout';
import { NoEventsFallback } from 'features/common/components/NoEventsFallback';
import { OrdersTable } from './components/OrdersTable/OrdersTable';
import { QueryResultProps } from './types';
import { RTKFilter } from 'features/common/components/Filters';
import { Search } from 'features/common/components/Search';
import { filterOptionOrdersPage } from 'features/common/components/Filters/constants';
import { getData } from 'services/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOrdersPage } from './hooks/useOrdersPage';
import { useTranslation } from 'react-i18next';

const RESULTS_PER_PAGE = 30;

export const Orders: FC = () => {
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { id: eventId } = useParams<IdParamsType>();

	const {
		searchText,
		setSearchText,
		selectedPeriod,
		selectedEvents,
		eventCount,
		eventFilterApplied,
		isNotFiltered,
		downloadOrdersQuery,
	} = useOrdersPage();

	const searchDebounce = useDebounce(setSearchText);

	const ordersQueryObject = useInfiniteQuery({
		queryKey: ['orders', selectedPeriod.applied, selectedEvents.applied, searchText],
		queryFn: ({ pageParam = 1 }) => {
			const event_ids = eventId ? eventId : eventFilterApplied;

			const searchParams = new URLSearchParams();
			searchParams.set('page', pageParam.toString());
			searchParams.set('per_page', RESULTS_PER_PAGE.toString());

			searchText && searchParams.set('search', searchText);
			event_ids && searchParams.set('event_ids', event_ids);
			selectedPeriod.applied && searchParams.set('date', selectedPeriod.applied);

			return getData(`/orders?${searchParams.toString()}`);
		},
		getNextPageParam: (lastPage, pages) => (lastPage.data.length >= RESULTS_PER_PAGE ? pages.length + 1 : undefined),
	}) as QueryResultProps;

	const displayFallback = isNotFiltered && !ordersQueryObject.data?.pages[0].data && searchText === '';

	const downloadClick = () => {
		downloadOrdersQuery.mutate();
		sendGAEvent({ category: 'Orders', action: 'Orders download' });
	};

	useEffect(() => {
		sendGAEvent({ category: 'Orders', action: 'Orders filter' });
	}, [selectedEvents, selectedPeriod]);

	return (
		<MainLayout>
			<MainHeader lessPadding gap="12px">
				<Icon cursor="pointer" iconName="arrowLeft" onClick={goBack} />
				<Styled.Title element="h1">{t('ordersPage-title')}</Styled.Title>
			</MainHeader>

			{displayFallback ? (
				<NoEventsFallback isFanDatabase={false} />
			) : (
				<>
					<Styled.CustomFlexContainer>
						<Search
							width="300px"
							onChange={(e) => searchDebounce(e.target.value)}
							placeholder={t('search-email-or-name')}
						/>
						<Styled.Buttons>
							<RTKFilter
								selectedPeriod={selectedPeriod}
								selectedEvents={selectedEvents}
								eventCount={eventCount}
								page="orders"
								filterOption={filterOptionOrdersPage}
							/>
							<Styled.CsvButton onClick={downloadClick}>{t('ordersPage-download')}</Styled.CsvButton>
						</Styled.Buttons>
					</Styled.CustomFlexContainer>
					<OrdersTable ordersQueryObject={ordersQueryObject} />
				</>
			)}
		</MainLayout>
	);
};
